import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookmarkSquareIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ChevronDownIcon,
  SparklesIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { AXIOS_INSTANCE } from '../../services/api-client';

type MemoryScope = 'user' | 'tenant';

interface InterviewMessage {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string | null;
}

interface InterviewConversation {
  id: string;
  workspace_id: number;
  created_at?: string | null;
  updated_at?: string | null;
  messages: InterviewMessage[];
}

interface InterviewCandidate {
  memory_text: string;
  memory_type: string;
  suggested_scope: MemoryScope;
  reason?: string | null;
}

interface RetrievalMemoryItem {
  id: number;
  memory_text: string;
  memory_type: string;
  scope: MemoryScope;
  can_edit: boolean;
  can_delete: boolean;
  version: number;
}

interface MemoryCompanionPanelProps {
  workspaceId: number;
}

const MEMORY_TYPE_LABELS: Record<string, string> = {
  assistant_response: 'Assistant',
  preference: 'Preference',
  profile: 'Profile',
  identity: 'Identity',
  constraint: 'Constraint',
};

function SectionToggle({
  eyebrow,
  title,
  description,
  badge,
  expanded,
  onToggle,
}: {
  eyebrow: string;
  title: string;
  description: string;
  badge: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="group flex w-full items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-4 text-left transition-[background-color,border-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.995] motion-reduce:transition-none hover:border-gray-300 dark:border-dark-border dark:bg-dark-surface dark:hover:border-gray-500"
    >
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-400">
          {eyebrow}
        </p>
        <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">{title}</h3>
        <p className="mt-1 hidden max-w-2xl text-xs leading-5 text-gray-500 dark:text-gray-400 md:block">{description}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:bg-dark-surface dark:text-gray-300">
          {badge}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 text-gray-400 transition-transform duration-300 group-hover:text-gray-600 dark:text-gray-400 dark:group-hover:text-gray-200 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
      </div>
    </button>
  );
}

export function MemoryCompanionPanel({ workspaceId }: MemoryCompanionPanelProps) {
  const [conversation, setConversation] = useState<InterviewConversation | null>(null);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const [candidates, setCandidates] = useState<InterviewCandidate[]>([]);
  const [candidateSaving, setCandidateSaving] = useState<Record<number, boolean>>({});
  const [candidateShareArmed, setCandidateShareArmed] = useState<Record<number, boolean>>({});

  const [memoryItems, setMemoryItems] = useState<RetrievalMemoryItem[]>([]);
  const [loadingMemory, setLoadingMemory] = useState(false);
  const [memoryError, setMemoryError] = useState<string | null>(null);
  const [showSavedMemory, setShowSavedMemory] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [savingMemory, setSavingMemory] = useState(false);
  const [newMemoryText, setNewMemoryText] = useState('');
  const [newMemoryType, setNewMemoryType] = useState('profile');
  const [manualShareArmed, setManualShareArmed] = useState(false);
  const [editingMemoryKey, setEditingMemoryKey] = useState<string | null>(null);
  const [editingMemoryText, setEditingMemoryText] = useState('');
  const [editingMemoryType, setEditingMemoryType] = useState('profile');

  const getMemoryItemKey = useCallback((item: Pick<RetrievalMemoryItem, 'id' | 'scope'>) => {
    return `${item.scope}:${item.id}`;
  }, []);

  const removeCandidateAtIndex = useCallback((index: number) => {
    setCandidates((prev) => prev.filter((_, i) => i !== index));
    setCandidateSaving((prev) => {
      const next: Record<number, boolean> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = value;
        if (idx > index) next[idx - 1] = value;
      });
      return next;
    });
    setCandidateShareArmed((prev) => {
      const next: Record<number, boolean> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = value;
        if (idx > index) next[idx - 1] = value;
      });
      return next;
    });
  }, []);

  const fetchSavedMemory = useCallback(async () => {
    setLoadingMemory(true);
    setMemoryError(null);
    try {
      const { data } = await AXIOS_INSTANCE.get('/api/retrieval/memory', {
        params: { limit: 100, include_tenant: true },
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      setMemoryItems(items);
    } catch (error: any) {
      setMemoryError(error?.response?.data?.detail || 'Failed to load memory.');
    } finally {
      setLoadingMemory(false);
    }
  }, []);

  const fetchConversation = useCallback(async () => {
    setLoadingConversation(true);
    setConversationError(null);
    try {
      const { data } = await AXIOS_INSTANCE.get(
        `/api/retrieval/interview/workspaces/${workspaceId}/conversation`
      );
      setConversation(data || null);
    } catch (error: any) {
      setConversationError(error?.response?.data?.detail || 'Failed to load interview conversation.');
    } finally {
      setLoadingConversation(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchConversation();
    fetchSavedMemory();
  }, [fetchConversation, fetchSavedMemory]);

  const interviewMessages = useMemo(() => conversation?.messages || [], [conversation]);

  const latestAssistantIndex = useMemo(() => {
    for (let i = interviewMessages.length - 1; i >= 0; i -= 1) {
      if (interviewMessages[i].role === 'assistant') {
        return i;
      }
    }
    return -1;
  }, [interviewMessages]);

  const currentQuestion = latestAssistantIndex >= 0 ? interviewMessages[latestAssistantIndex] : null;

  const transcriptMessages = useMemo(() => {
    if (latestAssistantIndex === -1) {
      return interviewMessages.slice(-8);
    }
    return interviewMessages
      .filter((_, index) => index !== latestAssistantIndex)
      .slice(-8);
  }, [interviewMessages, latestAssistantIndex]);

  const handleSend = useCallback(async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setConversationError(null);
    try {
      const { data } = await AXIOS_INSTANCE.post(
        `/api/retrieval/interview/workspaces/${workspaceId}/messages`,
        { content }
      );
      if (data?.conversation) {
        setConversation(data.conversation);
      }
      const nextCandidates: InterviewCandidate[] = Array.isArray(data?.memory_candidates)
        ? data.memory_candidates
        : [];
      setCandidates(nextCandidates);
      setCandidateSaving({});
      setCandidateShareArmed({});
      setDraft('');
    } catch (error: any) {
      setConversationError(error?.response?.data?.detail || 'Failed to send interview message.');
    } finally {
      setSending(false);
    }
  }, [draft, sending, workspaceId]);

  const handleConfirmCandidate = useCallback(async (index: number, scope: MemoryScope) => {
    const candidate = candidates[index];
    if (!candidate || candidateSaving[index]) return;
    setCandidateSaving((prev) => ({ ...prev, [index]: true }));
    setMemoryError(null);
    try {
      const { data } = await AXIOS_INSTANCE.post('/api/retrieval/memory/items', {
        memory_text: candidate.memory_text,
        memory_type: candidate.memory_type || 'profile',
        scope,
      });
      const savedItem = data?.item as RetrievalMemoryItem | undefined;
      if (savedItem?.id) {
        setMemoryItems((prev) => {
          const withoutDuplicate = prev.filter(
            (item) => !(item.id === savedItem.id && item.scope === savedItem.scope)
          );
          return [savedItem, ...withoutDuplicate];
        });
      }
      removeCandidateAtIndex(index);
      setShowSavedMemory(true);
      try {
        await fetchSavedMemory();
      } catch {
        // Keep the optimistic item visible if refresh fails.
      }
    } catch (error: any) {
      setMemoryError(error?.response?.data?.detail || 'Failed to save memory item.');
    } finally {
      setCandidateSaving((prev) => ({ ...prev, [index]: false }));
    }
  }, [candidateSaving, candidates, fetchSavedMemory, removeCandidateAtIndex]);

  const handleDismissCandidate = useCallback((index: number) => {
    removeCandidateAtIndex(index);
  }, [removeCandidateAtIndex]);

  const handleAddMemory = useCallback(async (scope: MemoryScope) => {
    const memoryText = newMemoryText.trim();
    if (!memoryText || savingMemory) return;

    setSavingMemory(true);
    setMemoryError(null);
    try {
      const { data } = await AXIOS_INSTANCE.post('/api/retrieval/memory/items', {
        memory_text: memoryText,
        memory_type: newMemoryType,
        scope,
      });
      const savedItem = data?.item as RetrievalMemoryItem | undefined;
      if (savedItem?.id) {
        setMemoryItems((prev) => {
          const withoutDuplicate = prev.filter(
            (item) => getMemoryItemKey(item) !== getMemoryItemKey(savedItem)
          );
          return [savedItem, ...withoutDuplicate];
        });
      }
      setNewMemoryText('');
      setNewMemoryType('profile');
      setManualShareArmed(false);
      setShowSavedMemory(true);
      await fetchSavedMemory();
    } catch (error: any) {
      setMemoryError(error?.response?.data?.detail || 'Failed to add memory.');
    } finally {
      setSavingMemory(false);
    }
  }, [fetchSavedMemory, getMemoryItemKey, newMemoryText, newMemoryType, savingMemory]);

  const handleStartEdit = useCallback((item: RetrievalMemoryItem) => {
    setEditingMemoryKey(getMemoryItemKey(item));
    setEditingMemoryText(item.memory_text);
    setEditingMemoryType(item.memory_type || 'profile');
  }, [getMemoryItemKey]);

  const handleCancelEdit = useCallback(() => {
    setEditingMemoryKey(null);
    setEditingMemoryText('');
    setEditingMemoryType('profile');
  }, []);

  const handleSaveEdit = useCallback(async (item: RetrievalMemoryItem) => {
    const memoryText = editingMemoryText.trim();
    if (!memoryText || savingMemory) return;

    setSavingMemory(true);
    setMemoryError(null);
    try {
      const { data } = await AXIOS_INSTANCE.patch(`/api/retrieval/memory/items/${item.id}`, {
        memory_text: memoryText,
        memory_type: editingMemoryType,
        scope: item.scope,
      });
      const savedItem = data?.item as RetrievalMemoryItem | undefined;
      if (savedItem?.id) {
        setMemoryItems((prev) =>
          prev.map((memoryItem) =>
            getMemoryItemKey(memoryItem) === getMemoryItemKey(item) ? savedItem : memoryItem
          )
        );
      }
      handleCancelEdit();
      await fetchSavedMemory();
    } catch (error: any) {
      setMemoryError(error?.response?.data?.detail || 'Failed to update memory.');
    } finally {
      setSavingMemory(false);
    }
  }, [editingMemoryText, editingMemoryType, fetchSavedMemory, getMemoryItemKey, handleCancelEdit, savingMemory]);

  const handleDeleteMemory = useCallback(async (item: RetrievalMemoryItem) => {
    if (savingMemory) return;

    setSavingMemory(true);
    setMemoryError(null);
    try {
      await AXIOS_INSTANCE.delete(`/api/retrieval/memory/items/${item.id}`, {
        params: { scope: item.scope },
      });
      setMemoryItems((prev) =>
        prev.filter((memoryItem) => getMemoryItemKey(memoryItem) !== getMemoryItemKey(item))
      );
      if (editingMemoryKey === getMemoryItemKey(item)) {
        handleCancelEdit();
      }
      await fetchSavedMemory();
    } catch (error: any) {
      setMemoryError(error?.response?.data?.detail || 'Failed to delete memory.');
    } finally {
      setSavingMemory(false);
    }
  }, [editingMemoryKey, fetchSavedMemory, getMemoryItemKey, handleCancelEdit, savingMemory]);

  return (
    <div className="h-full w-full bg-white dark:bg-dark-surface">
      <div className="mx-auto flex h-full max-w-3xl flex-col">
        <div className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-dark-border dark:bg-dark-surface/95">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                <SparklesIcon className="h-3.5 w-3.5" />
                Interview memory
              </div>
              <p className="mt-1 max-w-xl text-xs leading-5 text-gray-500 dark:text-gray-400">
                Capture only details that should still matter after this chat.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-700 dark:bg-dark-surface-2 dark:text-gray-300">
                {candidates.length} pending
              </span>
              <span className="rounded-full bg-gray-100 px-3 py-1 font-medium text-gray-700 dark:bg-dark-surface-2 dark:text-gray-300">
                {memoryItems.length} saved
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 pb-4 pt-3 md:px-4">
          <div className="space-y-3">
            {(conversationError || memoryError) && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/20 dark:text-rose-300">
                {conversationError || memoryError}
              </div>
            )}

            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-3 py-3 dark:border-dark-border">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                    Current question
                  </p>
                  <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">
                    Stay in interview mode
                  </h3>
                </div>
                <div className="inline-flex items-center gap-2 rounded-md bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-700 dark:bg-dark-surface-2 dark:text-gray-300">
                  <CheckCircleIcon className="h-4 w-4" />
                  Explicit confirmation required
                </div>
              </div>

              <div className="space-y-3 p-3">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
                  {loadingConversation ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Loading the interview thread...</p>
                  ) : currentQuestion ? (
                    <>
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                        <SparklesIcon className="h-3.5 w-3.5 text-eliza-red" />
                        Companion prompt
                      </div>
                      <p className="mt-2 whitespace-pre-wrap text-sm font-medium leading-6 text-charcoal dark:text-gray-100">
                        {currentQuestion.content}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                        <SparklesIcon className="h-3.5 w-3.5 text-eliza-red" />
                        Start here
                      </div>
                      <p className="mt-2 text-sm font-medium leading-6 text-charcoal dark:text-gray-100">
                        What is one preference, standing fact, or constraint that should change how AgentMesh answers for you?
                      </p>
                    </>
                  )}
                </div>

                <div className="rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface-2">
                  <div className="border-b border-gray-100 px-3 py-2 text-xs leading-5 text-gray-500 dark:border-dark-border dark:text-gray-400">
                    Turn the answer into one clear memory candidate, then review it before saving.
                  </div>
                  <div className="p-3">
                    <textarea
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      placeholder="Answer in plain language. Include the detail you want AgentMesh to remember."
                      aria-label="Interview answer"
                      className="min-h-[104px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-6 text-charcoal outline-none transition placeholder:text-gray-400 focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/15 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-eliza-red"
                    />
                    <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                      <p className="max-w-lg text-xs leading-5 text-gray-500 dark:text-gray-400">
                        Tip: answer naturally. The companion will reduce this to a reusable memory statement for review.
                      </p>
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={!draft.trim() || sending}
                        className="inline-flex h-11 items-center justify-center rounded-lg bg-charcoal px-5 text-sm font-medium text-white transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:bg-charcoal/90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-charcoal dark:hover:bg-gray-100"
                      >
                        {sending ? 'Sending answer...' : 'Send answer'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface">
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-3 py-3 dark:border-dark-border">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                    Review before saving
                  </p>
                  <h3 className="mt-1 text-sm font-semibold text-charcoal dark:text-gray-100">
                    Suggested memory from this turn
                  </h3>
                </div>
                <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-700 dark:bg-dark-surface-2 dark:text-gray-300">
                  {candidates.length} pending
                </span>
              </div>

              {candidates.length === 0 ? (
                <div className="px-3 py-3 text-sm leading-6 text-gray-500 dark:text-gray-400">
                  New suggestions appear here after each answer. That keeps the interview cadence simple: answer first, then review what is worth remembering.
                </div>
              ) : (
                <div className="space-y-2 p-3">
                  {candidates.map((candidate, index) => {
                    const saving = !!candidateSaving[index];
                    const shareArmed = !!candidateShareArmed[index];
                    const suggestedShared = candidate.suggested_scope === 'tenant';

                    return (
                      <article
                        key={`${candidate.memory_text}-${index}`}
                        className={`rounded-lg border px-3 py-3 transition-colors dark:border-dark-border dark:bg-dark-surface ${
                          suggestedShared
                            ? 'border-amber-300 bg-amber-50/40 dark:bg-amber-900/10'
                            : 'border-gray-200 bg-white dark:bg-dark-surface'
                        }`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-300">
                            {MEMORY_TYPE_LABELS[candidate.memory_type] || candidate.memory_type}
                          </span>
                          <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            Suggested: {suggestedShared ? 'share with team' : 'save for me'}
                          </span>
                        </div>

                        <p className="mt-2 whitespace-pre-wrap text-sm font-medium leading-6 text-charcoal dark:text-gray-100">
                          {candidate.memory_text}
                        </p>

                        {candidate.reason && (
                          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                            {candidate.reason}
                          </p>
                        )}

                        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-200/80 pt-3 dark:border-dark-border">
                          <button
                            type="button"
                            onClick={() => {
                              setCandidateShareArmed((prev) => ({ ...prev, [index]: false }));
                              void handleConfirmCandidate(index, 'user');
                            }}
                            disabled={saving}
                            className="inline-flex h-9 items-center gap-2 rounded-lg bg-charcoal px-3.5 text-sm font-medium text-white transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:bg-charcoal/90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-charcoal dark:hover:bg-gray-100"
                          >
                            <UserCircleIcon className="h-4 w-4" />
                            {saving ? 'Saving...' : 'Save for me'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (!shareArmed) {
                                setCandidateShareArmed((prev) => ({ ...prev, [index]: true }));
                                return;
                              }
                              void handleConfirmCandidate(index, 'tenant');
                            }}
                            disabled={saving}
                            className={`inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-sm font-medium transition-[background-color,border-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50 ${
                              shareArmed
                                ? 'border border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                                : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-gray-200 dark:hover:border-gray-500'
                            }`}
                          >
                            <BuildingOffice2Icon className="h-4 w-4" />
                            {saving ? 'Saving...' : shareArmed ? 'Confirm share with team' : 'Share with team'}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDismissCandidate(index)}
                            disabled={saving}
                            className="inline-flex h-9 items-center rounded-full px-3.5 text-sm font-medium text-gray-600 transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:bg-dark-surface-2"
                          >
                            Skip
                          </button>
                        </div>

                        {shareArmed && (
                          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
                            Shared memory can influence answers for other people in your organization. Confirm only if this should become common context.
                          </p>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <div className="grid gap-2 sm:grid-cols-2">
              <SectionToggle
                eyebrow="Conversation context"
                title="Earlier in this interview"
                description="Re-open prior turns if you need to see how the conversation has evolved."
                badge={`${interviewMessages.length} messages`}
                expanded={showTranscript}
                onToggle={() => setShowTranscript((prev) => !prev)}
              />

              <SectionToggle
                eyebrow="Archive"
                title="Saved memory"
                description="Review, edit, or add durable memory outside the live interview flow."
                badge={`${memoryItems.length} items`}
                expanded={showSavedMemory}
                onToggle={() => setShowSavedMemory((prev) => !prev)}
              />
            </div>

            {showTranscript && (
              <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
                {loadingConversation ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">Loading the interview thread...</p>
                ) : transcriptMessages.length === 0 ? (
                  <p className="text-sm leading-6 text-gray-500 dark:text-gray-400">
                    No earlier turns yet. Once the interview gets going, previous questions and answers will live here.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {transcriptMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`rounded-lg border px-3 py-3 ${
                          message.role === 'assistant'
                            ? 'border-gray-200 bg-gray-50 dark:border-dark-border dark:bg-dark-surface'
                            : 'border-gray-200 bg-white dark:border-dark-border dark:bg-dark-surface'
                        }`}
                      >
                        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                          {message.role === 'assistant' ? 'Companion' : 'You'}
                        </p>
                        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-charcoal dark:text-gray-100">
                          {message.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {showSavedMemory && (
              <section className="rounded-lg border border-gray-200 bg-white p-3 dark:border-dark-border dark:bg-dark-surface">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
                  <div className="flex items-start gap-3">
                    <BookmarkSquareIcon className="mt-0.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-charcoal dark:text-gray-100">
                        Add memory manually
                      </p>
                      <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                        Use this for facts that are already stable. Live interview suggestions above should still be your default capture path.
                      </p>
                    </div>
                  </div>
                  <textarea
                    value={newMemoryText}
                    onChange={(event) => {
                      setNewMemoryText(event.target.value);
                      setManualShareArmed(false);
                    }}
                    placeholder="Add a fact, preference, or constraint to remember..."
                    aria-label="Manual memory entry"
                    className="mt-3 min-h-[80px] w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm leading-6 text-charcoal outline-none transition placeholder:text-gray-400 focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/15 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-eliza-red"
                  />
                  <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <select
                      value={newMemoryType}
                      onChange={(event) => setNewMemoryType(event.target.value)}
                      className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/15 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100 dark:focus:border-eliza-red"
                    >
                      {Object.keys(MEMORY_TYPE_LABELS).map((type) => (
                        <option key={type} value={type}>
                          {MEMORY_TYPE_LABELS[type]}
                        </option>
                      ))}
                    </select>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setManualShareArmed(false);
                          void handleAddMemory('user');
                        }}
                        disabled={!newMemoryText.trim() || savingMemory}
                        className="inline-flex h-9 items-center gap-2 rounded-lg bg-charcoal px-3.5 text-sm font-medium text-white transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:bg-charcoal/90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-charcoal dark:hover:bg-gray-100"
                      >
                        <UserCircleIcon className="h-4 w-4" />
                        {savingMemory ? 'Saving...' : 'Remember for me'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!manualShareArmed) {
                            setManualShareArmed(true);
                            return;
                          }
                          void handleAddMemory('tenant');
                        }}
                        disabled={!newMemoryText.trim() || savingMemory}
                        className={`inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-sm font-medium transition-[background-color,border-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50 ${
                          manualShareArmed
                            ? 'border border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-200 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200'
                            : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-dark-border dark:bg-dark-surface dark:text-gray-200 dark:hover:border-gray-500'
                        }`}
                      >
                        <BuildingOffice2Icon className="h-4 w-4" />
                        {savingMemory ? 'Saving...' : manualShareArmed ? 'Confirm share with team' : 'Share with team'}
                      </button>
                    </div>
                  </div>
                  {manualShareArmed && (
                    <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
                      Shared memory can change answers for other people too. Confirm only if this belongs in common team context.
                    </p>
                  )}
                </div>

                <div className="mt-3 max-h-[24rem] space-y-2 overflow-auto pr-1">
                  {loadingMemory && (
                    <p className="text-sm text-stone-500 dark:text-gray-400">Loading saved memory...</p>
                  )}
                  {!loadingMemory && memoryItems.length === 0 && (
                    <p className="text-sm leading-6 text-stone-600 dark:text-gray-400">
                      No saved memory yet. Confirm a candidate above or add one manually here.
                    </p>
                  )}
                  {!loadingMemory && memoryItems.map((item) => {
                    const isEditing = editingMemoryKey === getMemoryItemKey(item);

                    return (
                      <div
                        key={getMemoryItemKey(item)}
                        className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2"
                      >
                        {isEditing ? (
                          <>
                            <textarea
                              value={editingMemoryText}
                              onChange={(event) => setEditingMemoryText(event.target.value)}
                              className="min-h-[72px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm leading-6 text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/15 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100 dark:focus:border-eliza-red"
                            />
                            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex flex-wrap items-center gap-2">
                                <select
                                  value={editingMemoryType}
                                  onChange={(event) => setEditingMemoryType(event.target.value)}
                                  className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-sm text-charcoal outline-none transition focus:border-eliza-red focus:ring-2 focus:ring-eliza-red/15 dark:border-dark-border dark:bg-dark-surface dark:text-gray-100 dark:focus:border-eliza-red"
                                >
                                  {Object.keys(MEMORY_TYPE_LABELS).map((type) => (
                                    <option key={type} value={type}>
                                      {MEMORY_TYPE_LABELS[type]}
                                    </option>
                                  ))}
                                </select>
                                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                  {item.scope === 'tenant' ? 'Shared memory' : 'Personal memory'}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  disabled={savingMemory}
                                  className="inline-flex h-9 items-center rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-[background-color,border-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-border dark:bg-dark-surface dark:text-gray-300 dark:hover:border-gray-500"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSaveEdit(item)}
                                  disabled={!editingMemoryText.trim() || savingMemory}
                                  className="inline-flex h-9 items-center rounded-lg bg-charcoal px-4 text-sm font-medium text-white transition-[background-color,color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none hover:bg-charcoal/90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-charcoal dark:hover:bg-gray-100"
                                >
                                  Save changes
                                </button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-start justify-between gap-3">
                              <p className="whitespace-pre-wrap text-sm leading-6 text-charcoal dark:text-gray-100">
                                {item.memory_text}
                              </p>
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {item.can_edit && (
                                  <button
                                    type="button"
                                    onClick={() => handleStartEdit(item)}
                                    disabled={savingMemory}
                                    className="text-xs font-medium text-gray-700 transition hover:text-charcoal disabled:opacity-50 dark:text-gray-300 dark:hover:text-white"
                                  >
                                    Edit
                                  </button>
                                )}
                                {item.can_delete && (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteMemory(item)}
                                    disabled={savingMemory}
                                    className="text-xs font-medium text-rose-600 transition hover:text-rose-500 disabled:opacity-50 dark:text-rose-400"
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-stone-700 shadow-sm dark:bg-dark-surface-2 dark:text-gray-300">
                                {MEMORY_TYPE_LABELS[item.memory_type] || item.memory_type}
                              </span>
                              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                {item.scope === 'tenant' ? 'Shared memory' : 'Personal memory'}
                              </span>
                              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-stone-500 dark:text-gray-500">
                                v{item.version}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
